import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import styles from './style.module.less';
import './GeminiContent.less';
import useSubtitleMqtt from '@/hooks/useSubtitleMqtt';

interface RealTimeContentProps {
  roomId: string;
  historyData: API.SubtitleItem[];
  // original: 只显示原文(实时转录);translation: 只显示译文(实时翻译),可选对照原文
  mode?: 'original' | 'translation';
  showSource?: boolean;
  // 实时翻译的目标语言:从各句 subtitle_translation_list 中挑对应语言;为空取第一条译文
  targetLang?: string;
}

export default function RealTimeContent({
  roomId, historyData, mode = 'original', showSource = false, targetLang,
}: RealTimeContentProps) {
  const { t } = useTranslation();
  const [messages, setMessages] = useState<API.SubtitleItem[] | undefined>(historyData);

  useSubtitleMqtt({
    roomId,
    onMessage: (msg) => {
      console.log('>> onMessage', msg);
      setMessages((prevData) => {
        if (!prevData) {
          return prevData;
        }

        const nextData = [...prevData];
        const index = nextData.findIndex((item) => item.subtitle_id === msg.subtitle_id);

        if (index > -1) {
          nextData[index] = msg;
        } else {
          nextData.push(msg);
        }

        return nextData;
      });
    },
  });

  useEffect(() => {
    console.log('..////////');
    // setMessages(historyData);
  }, [historyData]);

  // 静音会议（录音正常但全程无人说话）：不渲染空白页，给出明确提示
  // 复用 AI 转录 tab 的空态样式（gemini-content/loading-container），两个视图保持一致
  if (!messages?.length) {
    return (
      <div className="gemini-content">
        <div className="loading-container">
          <div className="loading-text">{t('meeting.noSpeechContent')}</div>
        </div>
      </div>
    );
  }

  // 每句相对会议开始(第一句)的时间戳 mm:ss / h:mm:ss
  // 用 dayjs 解析:后端 gmt_generate 格式(如 "2026-09-17 13:57:08")Safari/部分浏览器
  // 的 new Date() 解析会得到 Invalid Date,导致时间不显示;dayjs 更宽松
  const start = messages?.[0]?.gmt_generate ? dayjs(messages[0].gmt_generate) : null;
  const relTime = (gmt?: string) => {
    if (!gmt || !start || !start.isValid()) return '';
    const cur = dayjs(gmt);
    if (!cur.isValid()) return '';
    const sec = Math.max(0, cur.diff(start, 'second'));
    const pad = (n: number) => String(n).padStart(2, '0');
    const h = Math.floor(sec / 3600);
    const body = `${pad(Math.floor((sec % 3600) / 60))}:${pad(sec % 60)}`;
    return h > 0 ? `${h}:${body}` : body;
  };

  return (
        <article>
            {messages
                && messages.map((item) => {
                  // 目标语言优先取对应译文,没有则回退第一条译文(实时翻译只有会中广播过的语言)
                  const list = item.subtitle_translation_list;
                  const matched = targetLang ? list?.find((tr) => tr.lang === targetLang) : undefined;
                  const subText = (matched || list?.[0])?.text;
                  // 实时翻译:主体显示译文,勾选「对照原文」时下方灰字显示原文;
                  // 实时转录:只显示原文
                  if (mode === 'translation') {
                    return (
                        <div key={item.subtitle_id} className={styles.subtitle}>
                            <span className={styles.subtitleTime}>{relTime(item.gmt_generate)}</span>
                            <p className={styles.original}>{subText || item.text}</p>
                            {showSource && subText && <p className={styles.translateText}>{item.text}</p>}
                        </div>
                    );
                  }
                  return (
                      <div key={item.subtitle_id} className={styles.subtitle}>
                          <span className={styles.subtitleTime}>{relTime(item.gmt_generate)}</span>
                          <p className={styles.original}>{item.text}</p>
                      </div>
                  );
                })}
        </article>
  );
}
